import { ThemeProvider } from 'Contexts/ThemeContext';
import { AppLoadingProvider } from 'Contexts/AppLoadingContext';
import { RouterProvider } from 'react-router-dom';
import { router } from './Routes/AppRouter';
import Splash from './Components/layout/Splash/index.js';
import { useAppLoading } from './contexts/AppLoadingContext';
import { UserProvider } from './contexts/UserContext';
import { WalletProvider } from './contexts/WalletContext';
import { CloverProvider } from './contexts/CloverContext';
import { TokenProvider } from './contexts/TokenContext';
import { InboxProvider } from './contexts/InboxContext';
import { ClarityInit } from './Components/observability/ClarityInit.js';
import { GAInit } from './Components/observability/GAInit.js';
import { NewRelicInit } from './Components/observability/NewRelicInit.js';
import { EarlyUserIdentificationProvider } from './contexts/EarlyUserIdentificationContext';
import WhatsappFloatButton from './Components/features/WhatsappFloatButton/index.js';
import ConnectionBanner from './Components/common/ConnectionBanner/index.js';
import '@/styles/App.css';
import '@/styles/containerStyle.less';
import '@/styles/background.less';
import '@/styles/fonts.less';
import { Helmet } from 'react-helmet';
import { useLogos } from './Hooks/useLogos';
import { useEffect, useState } from 'react';
import { initializeTenant, getTenantId } from './services/tenant/tenantService.js';
import { Toaster } from 'react-hot-toast';
import { applyThemeVariables } from './utils/themeUtils';

const AppContent = () => {
  const { isLoading } = useAppLoading();
  const { getLogo } = useLogos();

  useEffect(() => {
    if (getLogo("title", "name")) {
      document.title = getLogo("title", "name");
    }
  }, [getLogo]);

  if (isLoading) {
    return <Splash />;
  }

  return (
    <>
      <ClarityInit />
      <GAInit />
      <NewRelicInit />
      <RouterProvider router={router} />
      <WhatsappFloatButton />
    </>
  );
};

function App() {
  const [isInitialized, setIsInitialized] = useState(() => !!getTenantId());
  const [tenantId, setTenantId] = useState(() => getTenantId() || null);
  const { getLogo, theme } = useLogos();

  const defaultTitle = "Carregando...";
  const defaultFavicon = "/favicon.ico";
  const defaultAppleIcon = "/logo192.png";

  useEffect(() => {
    if (isInitialized) return;

    const initTenant = async () => {
      try {
        await initializeTenant();
        const currentTenantId = getTenantId();
        if (!currentTenantId) {
          throw new Error('Tenant ID not found after initialization');
        }
        setTenantId(currentTenantId);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize tenant:', error);
        setIsInitialized(true);
      }
    };

    initTenant();

    window.addEventListener('message', (event) => {
      if (event.data.type === 'UPDATE_THEME' && event.data.theme) {
        localStorage.setItem('theme-management', JSON.stringify(event.data.theme));
        applyThemeVariables(event.data.theme);
      }
    });
  }, []);

  if (!isInitialized || !tenantId) {
    return <Splash />;
  }

  return (
    <>
      <Toaster />
      <ConnectionBanner />
      <Helmet>
        <title>{getLogo("title", "name") || defaultTitle}</title>
        <meta name="description" content={getLogo("title", "description") || "Uma plataforma Mova Incentivos."} />
        
        <meta property="og:title" content={getLogo("title", "name") || defaultTitle} />
        <meta property="og:description" content={getLogo("title", "description") || "Uma plataforma Mova Incentivos."} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content={getLogo("logo", "og") || `${process.env.REACT_APP_S3_BASE_URL}/${tenantId}/assets/images/og-image.png`} />
        <meta property="og:site_name" content={getLogo("title", "name") || defaultTitle} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getLogo("title", "name") || defaultTitle} />
        <meta name="twitter:description" content={getLogo("title", "description") || "Uma plataforma Mova Incentivos."} />
        <meta name="twitter:image" content={getLogo("logo", "og") || `${process.env.REACT_APP_S3_BASE_URL}/${tenantId}/assets/images/og-image.png`} />
        
        <link rel="icon" href={getLogo("logo", "favicon") || defaultFavicon} />
        <link rel="apple-touch-icon" href={getLogo("logo", "default") || defaultAppleIcon} />
        <link rel="manifest" href="/manifest.json" />
        
        {theme?.common?.style && (
          <link rel="stylesheet" href={theme.common.style} />
        )}
      </Helmet>
      <ThemeProvider clientId={tenantId}>
        <AppLoadingProvider clientId={tenantId}>
          <TokenProvider>
            <UserProvider>
              <EarlyUserIdentificationProvider>
                <WalletProvider>
                  <CloverProvider>
                    <InboxProvider>
                      <AppContent />
                    </InboxProvider>
                  </CloverProvider>
                </WalletProvider>
              </EarlyUserIdentificationProvider>
            </UserProvider>
          </TokenProvider>
        </AppLoadingProvider>
      </ThemeProvider>
    </>
  );
}

export default App;
